// ================================
// DELIVERY MINI MAP
// A glanceable embedded view of the delivery stop and the driver's own
// live position — not turn-by-turn navigation. "Navigate" still hands off
// to Google Maps for actual driving directions; this is just a quick
// visual check of where the stop is relative to the driver right now.
// ================================
import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';

// Leaflet's default marker images don't resolve under webpack — plain
// divIcons sidestep that entirely (same pattern as the admin live map).
const driverPin = new L.DivIcon({
  className: 'driver-map-pin',
  html: `<div style="background:#1e6bff;color:#fff;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4);">🚗</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14]
});
const destinationPin = new L.DivIcon({
  className: 'destination-map-pin',
  html: `<div style="background:#c0101a;width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4);"></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 22]
});

export default function DeliveryMiniMap({ oid, address, myLocation }) {
  const [destination, setDestination] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    axios.get(`/api/orders/${oid}/destination`)
      .then(r => { if (!cancelled) setDestination(r.data); })
      .catch(e => { if (!cancelled) setError(e.response?.data?.error || "Couldn't locate that address"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [oid]);

  if (loading) {
    return (
      <div style={{ padding: 18, textAlign: 'center', fontSize: 12, color: 'var(--text-secondary)' }}>
        Loading map…
      </div>
    );
  }
  if (error || !destination) {
    return (
      <div style={{ padding: 18, textAlign: 'center', fontSize: 12, color: 'var(--text-secondary)' }}>
        {error || "Couldn't locate that address"}
      </div>
    );
  }

  const center = myLocation
    ? [(myLocation.lat + destination.lat) / 2, (myLocation.lng + destination.lng) / 2]
    : [destination.lat, destination.lng];

  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)' }}>
      <MapContainer center={center} zoom={myLocation ? 13 : 14} style={{ height: 220, width: '100%' }} scrollWheelZoom={false}>
        {/* CARTO Voyager — same swap as the admin live map, see its comment
            for why (free, no API key, actually readable roads/labels). */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          subdomains="abcd"
          maxZoom={20}
        />
        {myLocation && (
          <Marker position={[myLocation.lat, myLocation.lng]} icon={driverPin}>
            <Popup>You are here</Popup>
          </Marker>
        )}
        <Marker position={[destination.lat, destination.lng]} icon={destinationPin}>
          <Popup>{address}</Popup>
        </Marker>
        {myLocation && (
          <Polyline
            positions={[[myLocation.lat, myLocation.lng], [destination.lat, destination.lng]]}
            pathOptions={{ color: '#1e6bff', weight: 2, dashArray: '6 6' }}
          />
        )}
      </MapContainer>
    </div>
  );
}
